// src/components/Preview3D.js
import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { createGeometry } from '../utils/geometryHelpers';
import './Preview3D.css';

const Preview3D = ({ geometryParams, onConfirm, onCancel, isConfirming }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const animationIdRef = useRef(null);
  
  // 添加状态来存储所有几何体
  const allMeshesRef = useRef([]);
  // 添加状态来存储基础模型信息
  const [baseModelInfo, setBaseModelInfo] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [viewType, setViewType] = useState('perspective');
  const [zoom, setZoom] = useState(1);

  // 初始化Three.js场景
  useEffect(() => {
    if (!mountRef.current) return;

    // 1. 创建场景
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    // 2. 创建相机
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 150;
    cameraRef.current = camera;

    // 3. 创建渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // 4. 添加轨道控制器
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 10;
    controls.maxDistance = 500;
    controls.maxPolarAngle = Math.PI;
    
    controls.autoRotate = false;
    controls.enableRotate = true;
    controls.enableZoom = true;
    controls.enablePan = true;
    
    controlsRef.current = controls;

    // 5. 添加灯光
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 20, 15);
    scene.add(directionalLight);

    // 6. 添加坐标轴辅助
    const axesHelper = new THREE.AxesHelper(50);
    scene.add(axesHelper);

    // 7. 添加网格地面
    const gridHelper = new THREE.GridHelper(200, 20, 0x444444, 0x888888);
    gridHelper.position.y = -50;
    scene.add(gridHelper);

    // 8. 渲染循环
    const animate = () => {
      if (isRotating && allMeshesRef.current.length > 0) {
        allMeshesRef.current.forEach(mesh => {
          mesh.rotation.y += 0.005;
          mesh.rotation.x += 0.002;
        });
      }
      
      controls.update();
      
      renderer.render(scene, camera);
      animationIdRef.current = requestAnimationFrame(animate);
    };
    
    animate();

    // 9. 窗口大小调整
    const handleResize = () => {
      if (!mountRef.current || !camera || !renderer) return;
      
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };
    
    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
      if (renderer) {
        renderer.dispose();
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      // 清理所有几何体
      allMeshesRef.current = [];
      // 清理基础模型信息
      setBaseModelInfo(null);
    };
  }, []);

  // 更新几何体 - 修复孔的位置计算
  useEffect(() => {
    if (!sceneRef.current || !geometryParams) return;

    // 如果是孔，添加到现有场景中，不清除其他几何体
    if (geometryParams.type === 'hole') {
      // 创建孔的几何体
      const geometryData = createGeometry(geometryParams);
      if (!geometryData) return;

      let geometry, material;
      
      if (geometryData.geometryType === 'CylinderGeometry') {
        geometry = new THREE.CylinderGeometry(...geometryData.args);
        // 孔用红色表示
        material = new THREE.MeshPhongMaterial({
          color: 0xff4757,
          specular: 0x555555,
          shininess: 30,
          transparent: true,
          opacity: 0.8
        });
      } else {
        return; // 如果不是圆柱体几何，不创建
      }

      // 创建孔的网格
      const holeMesh = new THREE.Mesh(geometry, material);
      holeMesh.castShadow = true;
      holeMesh.receiveShadow = true;
      
      // 设置孔的位置 - 使用实际的基础模型尺寸
      let cubeHalfSize = 25; // 默认值：50mm立方体的一半
      
      // 从基础模型信息中获取实际尺寸
      if (baseModelInfo) {
        if (baseModelInfo.type === 'cube' && baseModelInfo.size) {
          cubeHalfSize = baseModelInfo.size / 2;
          console.log(`📏 使用实际立方体尺寸: ${baseModelInfo.size}mm, 半高: ${cubeHalfSize}mm`);
        } else if (baseModelInfo.type === 'cuboid') {
          // 对于长方体，使用高度方向的一半
          if (baseModelInfo.height) {
            cubeHalfSize = baseModelInfo.height / 2;
            console.log(`📏 使用长方体高度: ${baseModelInfo.height}mm, 半高: ${cubeHalfSize}mm`);
          }
        }
      } else {
        console.warn(`⚠️ 未找到基础模型信息，使用默认尺寸: ${cubeHalfSize * 2}mm`);
      }
      
      const xOffset = geometryParams.x || 0;
      const yOffset = geometryParams.y || 0;
      const holeDepth = geometryParams.depth || 7.0;

      console.log(`🔧 孔参数: 
        类型: ${geometryParams.type}
        直径: ${geometryParams.diameter}mm
        深度: ${holeDepth}mm
        位置: (${xOffset}, ${yOffset})mm
        表面: ${geometryParams.surface}
        基础模型半高: ${cubeHalfSize}mm`);

      // 根据表面类型设置位置和旋转
      // Three.js坐标系: X向右, Y向上, Z向前
      const epsilon = 0.01; // 0.01单位偏移，大约0.01mm

// 修改switch语句中的位置计算
switch(geometryParams.surface) {
  case 'top': // 上表面
    // 孔从上表面向下延伸，添加微小向外偏移
    holeMesh.position.set(
      xOffset, 
      cubeHalfSize - holeDepth/2 + epsilon, // 向外偏移epsilon
      yOffset
    );
    console.log(`📌 孔位置(top): 
      X=${xOffset}mm, 
      Y=${cubeHalfSize - holeDepth/2 + epsilon}mm (立方体顶部: ${cubeHalfSize}mm - 孔半高: ${holeDepth/2}mm + 偏移: ${epsilon}mm), 
      Z=${yOffset}mm`);
    break;
    
  case 'front': // 前表面
    // 孔从前表面向后延伸，添加微小向外偏移
    holeMesh.position.set(
      xOffset, 
      yOffset, 
      -cubeHalfSize + holeDepth/2 - epsilon // 向外偏移-epsilon
    );
    holeMesh.rotation.x = Math.PI / 2; // 绕X轴旋转90度
    console.log(`📌 孔位置(front): 
      X=${xOffset}mm, 
      Y=${yOffset}mm, 
      Z=${-cubeHalfSize + holeDepth/2 - epsilon}mm (立方体前面: ${-cubeHalfSize}mm + 孔半高: ${holeDepth/2}mm - 偏移: ${epsilon}mm)`);
    break;
    
  case 'side': // 侧面（右侧）
    // 孔从右侧面向左延伸，添加微小向外偏移
    holeMesh.position.set(
      cubeHalfSize - holeDepth/2 + epsilon, // 向外偏移epsilon
      yOffset, 
      xOffset
    );
    holeMesh.rotation.z = Math.PI / 2; // 绕Z轴旋转90度
    console.log(`📌 孔位置(side): 
      X=${cubeHalfSize - holeDepth/2 + epsilon}mm (立方体右侧: ${cubeHalfSize}mm - 孔半高: ${holeDepth/2}mm + 偏移: ${epsilon}mm), 
      Y=${yOffset}mm, 
      Z=${xOffset}mm`);
    break;
          
        default:
          console.warn(`❌ 未知的表面类型: ${geometryParams.surface}`);
          return;
      }

      // 添加孔到场景
      sceneRef.current.add(holeMesh);
      allMeshesRef.current.push(holeMesh);
      
      console.log(`✅ 孔已添加到场景`);
      
    } else {
      // 对于其他几何体（立方体、圆柱体等），清除之前的几何体
      
      // 移除旧的几何体
      allMeshesRef.current.forEach(mesh => {
        sceneRef.current.remove(mesh);
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) mesh.material.dispose();
      });
      allMeshesRef.current = [];

      // 创建新的几何体
      const geometryData = createGeometry(geometryParams);
      if (!geometryData) return;

      let geometry;
      let material;
      
      switch(geometryData.geometryType) {
        case 'BoxGeometry':
          geometry = new THREE.BoxGeometry(...geometryData.args);
          material = new THREE.MeshPhongMaterial({
            color: 0x4a90e2,
            specular: 0x555555,
            shininess: 30,
            transparent: true,
            opacity: 0.85
          });
          break;
        case 'CylinderGeometry':
          geometry = new THREE.CylinderGeometry(...geometryData.args);
          material = new THREE.MeshPhongMaterial({
            color: 0x48bb78,
            specular: 0x555555,
            shininess: 30,
            transparent: true,
            opacity: 0.85
          });
          break;
        case 'SphereGeometry':
          geometry = new THREE.SphereGeometry(...geometryData.args);
          material = new THREE.MeshPhongMaterial({
            color: 0xff9800,
            specular: 0x555555,
            shininess: 30,
            transparent: true,
            opacity: 0.85
          });
          break;
        default:
          return;
      }

      // 创建网格
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      
      // 几何体居中显示
      geometry.computeBoundingBox();
      const center = new THREE.Vector3();
      geometry.boundingBox.getCenter(center);
      geometry.translate(-center.x, -center.y, -center.z);
      
      // 保存基础模型信息
      const newBaseModelInfo = {
        type: geometryParams.type,
        size: geometryParams.size || null,
        length: geometryParams.length || null,
        width: geometryParams.width || null,
        height: geometryParams.height || null,
        diameter: geometryParams.diameter || null,
        position: mesh.position,
        geometryParams: { ...geometryParams } // 保存完整参数
      };
      
      setBaseModelInfo(newBaseModelInfo);
      
      console.log(`📊 创建基础模型: 
        类型: ${geometryParams.type}
        尺寸: ${JSON.stringify(geometryParams)}
        位置: (${mesh.position.x}, ${mesh.position.y}, ${mesh.position.z})
        已保存基础模型信息`);
      
      sceneRef.current.add(mesh);
      allMeshesRef.current.push(mesh);
    }

    // 调整相机位置
    if (cameraRef.current && allMeshesRef.current.length > 0) {
      // 创建一个包含所有几何体的包围盒
      const boundingBox = new THREE.Box3();
      allMeshesRef.current.forEach(mesh => {
        boundingBox.expandByObject(mesh);
      });
      
      const size = boundingBox.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = cameraRef.current.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / Math.sin(fov / 2));
      
      cameraZ *= 1.5;
      cameraRef.current.position.z = cameraZ;
      cameraRef.current.lookAt(0, 0, 0);
      
      if (controlsRef.current) {
        controlsRef.current.update();
      }
      
      console.log(`📷 相机调整: 
        包围盒尺寸: ${size.x.toFixed(1)}×${size.y.toFixed(1)}×${size.z.toFixed(1)}
        相机距离: ${cameraZ.toFixed(1)}`);
    }

  }, [geometryParams, baseModelInfo]);

  // 处理自动旋转切换
  const handleRotationToggle = () => {
    const newIsRotating = !isRotating;
    setIsRotating(newIsRotating);
    
    if (controlsRef.current) {
      if (newIsRotating) {
        controlsRef.current.autoRotate = true;
        controlsRef.current.autoRotateSpeed = 1.0;
        controlsRef.current.enableRotate = false;
      } else {
        controlsRef.current.autoRotate = false;
        controlsRef.current.enableRotate = true;
      }
    }
  };

  // 视图控制
  const handleViewChange = (view) => {
    setViewType(view);
    if (!cameraRef.current || !controlsRef.current) return;
    
    switch(view) {
      case 'front':
        cameraRef.current.position.set(0, 0, 150);
        cameraRef.current.lookAt(0, 0, 0);
        break;
      case 'top':
        cameraRef.current.position.set(0, 150, 0);
        cameraRef.current.lookAt(0, 0, 0);
        break;
      case 'side':
        cameraRef.current.position.set(150, 0, 0);
        cameraRef.current.lookAt(0, 0, 0);
        break;
      default:
        cameraRef.current.position.set(100, 100, 150);
        cameraRef.current.lookAt(0, 0, 0);
    }
    
    if (controlsRef.current) {
      controlsRef.current.update();
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
    if (cameraRef.current) {
      cameraRef.current.zoom = zoom * 1.2;
      cameraRef.current.updateProjectionMatrix();
    }
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.5));
    if (cameraRef.current) {
      cameraRef.current.zoom = zoom / 1.2;
      cameraRef.current.updateProjectionMatrix();
    }
  };

  const handleResetView = () => {
    setZoom(1);
    setViewType('perspective');
    setIsRotating(false);
    
    if (cameraRef.current) {
      cameraRef.current.position.set(100, 100, 150);
      cameraRef.current.zoom = 1;
      cameraRef.current.lookAt(0, 0, 0);
      cameraRef.current.updateProjectionMatrix();
    }
    
    if (controlsRef.current) {
      controlsRef.current.reset();
      controlsRef.current.autoRotate = false;
      controlsRef.current.enableRotate = true;
    }
  };

  // 清理所有几何体
  const handleClearAll = () => {
    if (!sceneRef.current) return;
    
    allMeshesRef.current.forEach(mesh => {
      sceneRef.current.remove(mesh);
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) mesh.material.dispose();
    });
    allMeshesRef.current = [];
    setBaseModelInfo(null);
    
    console.log('🧹 已清除所有几何体和基础模型信息');
  };

  // 如果没有几何参数，显示占位
  if (!geometryParams) {
    return (
      <div className="preview-container empty">
        <div className="empty-preview">
          <div className="placeholder-icon">🎨</div>
          <h3>3D 预览</h3>
          <p>等待 AI 解析几何参数...</p>
          <div className="hint">
            💡 尝试输入：
            <ul>
              <li>"创建立方体，边长50mm"</li>
              <li>"做一个直径30mm，高60mm的圆柱体"</li>
              <li>"创建一个半径25mm的球体"</li>
              <li>"制作长方体 40×30×50mm"</li>
              <li>"创建六棱柱，内切圆直径30mm，高50mm"</li>
              <li>"在上表面打一个直径4mm，深度5mm的孔"</li>
              <li>"在前表面(x=10mm, y=20mm)位置打孔，直径6mm，深度8mm"</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // 计算当前显示的几何体数量
  const baseModelCount = allMeshesRef.current.filter(mesh => 
    mesh.material && mesh.material.color && mesh.material.color.getHex() !== 0xff4757
  ).length;
  const holeCount = allMeshesRef.current.filter(mesh => 
    mesh.material && mesh.material.color && mesh.material.color.getHex() === 0xff4757
  ).length;

  return (
    <div className="preview-container">
      <div className="preview-header">
        <h3>🎨 3D 预览</h3>
        <div className="preview-status">
          <span className="status-indicator active"></span>
          <span>模型就绪 • 鼠标拖拽旋转 • 滚轮缩放</span>
          {allMeshesRef.current.length > 0 && (
            <span className="model-count">
              ({baseModelCount}个基础模型 + {holeCount}个孔)
            </span>
          )}
          {baseModelInfo && (
            <span className="base-model-info">
              • 基础模型: {baseModelInfo.type === 'cube' ? `立方体 ${baseModelInfo.size}mm` : 
                         baseModelInfo.type === 'cuboid' ? `长方体 ${baseModelInfo.length}×${baseModelInfo.width}×${baseModelInfo.height}mm` :
                         baseModelInfo.type === 'cylinder' ? `圆柱体 直径${baseModelInfo.diameter}mm 高${baseModelInfo.height}mm` : 
                         baseModelInfo.type}
            </span>
          )}
        </div>
      </div>
      
      <div className="preview-canvas" ref={mountRef}>
        {/* Three.js画布会渲染在这里 */}
      </div>
      
      <div className="preview-controls">
        <div className="view-controls">
          <button 
            className={`view-btn ${viewType === 'perspective' ? 'active' : ''}`}
            onClick={() => handleViewChange('perspective')}
            title="透视图"
          >
            🔍
          </button>
          <button 
            className={`view-btn ${viewType === 'front' ? 'active' : ''}`}
            onClick={() => handleViewChange('front')}
            title="前视图"
          >
            ⬆️
          </button>
          <button 
            className={`view-btn ${viewType === 'top' ? 'active' : ''}`}
            onClick={() => handleViewChange('top')}
            title="俯视图"
          >
            ⬇️
          </button>
          <button 
            className={`view-btn ${viewType === 'side' ? 'active' : ''}`}
            onClick={() => handleViewChange('side')}
            title="侧视图"
          >
            ➡️
          </button>
        </div>
        
        <div className="zoom-controls">
          <button onClick={handleZoomOut} title="缩小">➖</button>
          <span>{(zoom * 100).toFixed(0)}%</span>
          <button onClick={handleZoomIn} title="放大">➕</button>
        </div>
        
        <div className="rotation-control">
          <label>
            <input 
              type="checkbox" 
              checked={isRotating}
              onChange={handleRotationToggle}
            />
            自动旋转
          </label>
          <button onClick={handleResetView} title="重置视图">🔄</button>
          {allMeshesRef.current.length > 0 && (
            <button 
              onClick={handleClearAll}
              className="clear-btn"
              title="清除所有模型"
            >
              🗑️ 清除
            </button>
          )}
        </div>
      </div>
      
      {/* 确认面板 */}
      <div className="confirmation-panel">
        <div className="params-display">
          <h4>📐 参数确认</h4>
          <div className="params-grid">
            <div className="param-item">
              <span className="param-label">当前类型：</span>
              <span className="param-value">
                {geometryParams.type === 'cube' ? '立方体' : 
                 geometryParams.type === 'cylinder' ? '圆柱体' : 
                 geometryParams.type === 'hole' ? '孔' : 
                 geometryParams.type === 'sphere' ? '球体' : 
                 geometryParams.type === 'cuboid' ? '长方体' :
                 geometryParams.type === 'polygon_prism' ? `${geometryParams.sides || 6}棱柱` : '未知'}
              </span>
            </div>
            
            {/* 孔的参数显示 */}
            {geometryParams.type === 'hole' && (
              <>
                <div className="param-item">
                  <span className="param-label">直径：</span>
                  <span className="param-value">{geometryParams.diameter}mm</span>
                </div>
                <div className="param-item">
                  <span className="param-label">深度：</span>
                  <span className="param-value">{geometryParams.depth}mm</span>
                </div>
                <div className="param-item">
                  <span className="param-label">位置：</span>
                  <span className="param-value">({geometryParams.x}, {geometryParams.y})mm</span>
                </div>
                <div className="param-item">
                  <span className="param-label">面：</span>
                  <span className="param-value">
                    {geometryParams.surface === 'top' ? '上表面' :
                     geometryParams.surface === 'front' ? '前表面' :
                     geometryParams.surface === 'side' ? '侧面' : geometryParams.surface}
                  </span>
                </div>
                <div className="param-item">
                  <span className="param-label">坐标系：</span>
                  <span className="param-value">以面中心为原点</span>
                </div>
              </>
            )}

            {/* 立方体参数 */}
            {geometryParams.size && geometryParams.type === 'cube' && (
              <div className="param-item">
                <span className="param-label">边长：</span>
                <span className="param-value">{geometryParams.size}mm</span>
              </div>
            )}
            
            {/* 长方体参数 */}
            {geometryParams.type === 'cuboid' && (
              <>
                <div className="param-item">
                  <span className="param-label">长度：</span>
                  <span className="param-value">{geometryParams.length}mm</span>
                </div>
                <div className="param-item">
                  <span className="param-label">宽度：</span>
                  <span className="param-value">{geometryParams.width}mm</span>
                </div>
                <div className="param-item">
                  <span className="param-label">高度：</span>
                  <span className="param-value">{geometryParams.height}mm</span>
                </div>
              </>
            )}
            
            {/* 圆柱体参数 */}
            {geometryParams.type === 'cylinder' && geometryParams.type !== 'hole' && (
              <>
                <div className="param-item">
                  <span className="param-label">直径：</span>
                  <span className="param-value">{geometryParams.diameter}mm</span>
                </div>
                <div className="param-item">
                  <span className="param-label">高度：</span>
                  <span className="param-value">{geometryParams.height}mm</span>
                </div>
              </>
            )}
            
            {/* 多棱柱参数 */}
            {geometryParams.type === 'polygon_prism' && (
              <>
                <div className="param-item">
                  <span className="param-label">边数：</span>
                  <span className="param-value">{geometryParams.sides}</span>
                </div>
                <div className="param-item">
                  <span className="param-label">内切圆直径：</span>
                  <span className="param-value">{geometryParams.inscribed_diameter}mm</span>
                </div>
                <div className="param-item">
                  <span className="param-label">高度：</span>
                  <span className="param-value">{geometryParams.height}mm</span>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="action-buttons">
          <button 
            className="btn-confirm"
            onClick={() => onConfirm && onConfirm(geometryParams)}
            disabled={isConfirming}
          >
            {isConfirming ? '⏳ 确认中...' : '✅ 确认建模'}
          </button>
          <button 
            className="btn-modify"
            onClick={() => onCancel && onCancel()}
            disabled={isConfirming}
          >
            ✏️ 修改参数
          </button>
        </div>
      </div>
    </div>
  );
};

export default Preview3D;